import React from 'react'
import { LoadingButton } from '@common/material'
import { localizedStrings } from '@core/strings'
import { Box, Flex } from '@fundamentals'
import { ActionCard } from '@elements'
import { Permit } from '@core/entities/permit/Permit'
import moment from 'moment'
import { useModal } from '@common/GlobalModal'
import RejectResumePermitRequestModal from '../modals/RejectResumePermitRequestModal'
import {
  useApproveResumePermitRequest,
  useRejectResumePermitRequest,
} from '@core/react-query/features/permits/permit'
import { useToast } from '@core/toast'
import {
  ApprovePermitDetailsSidebarData,
  RejectResumePermitSubmitData,
} from '../types'

type PermitSuspensionCardProps = {
  permit: Permit
}
export const PermitSuspensionCard: React.FC<PermitSuspensionCardProps> = ({
  permit,
}) => {
  const { showModal, hideModal } = useModal()
  const toast = useToast()
  const approveResumeRequest = useApproveResumePermitRequest()
  const rejectResumeRequest = useRejectResumePermitRequest()

  if (
    !(
      permit?.permitSuspensionResult?.isPending() &&
      permit.hasPermission('APPROVE_SUSPENSION_RESUME_REQUEST')
    )
  )
    return null

  const { suspendedBy, startedOn } = permit.permitSuspensionResult
  const datetimeString = moment(startedOn).format('D MMMM YYYY HH:mm')

  const handleApproveResumeRequest = (
    data: ApprovePermitDetailsSidebarData,
  ) => {
    approveResumeRequest.mutate(data, {
      onSuccess: () => {
        hideModal()
        toast.success(localizedStrings.resumeRequestApproved)
      },
      onError: (e) => toast.error(e.message),
    })
  }

  const handleRejectResumeRequest = (data: RejectResumePermitSubmitData) => {
    rejectResumeRequest.mutate(data, {
      onSuccess: () => {
        hideModal()
        toast.success(localizedStrings.resumeRequestRejected)
      },
      onError: (e) => toast.error(e.message),
    })
  }

  const handleApprove = () => {
    handleApproveResumeRequest({ permitId: permit.id })
  }

  const handleReject = (comment: string) => {
    handleRejectResumeRequest({ permitId: permit.id, comment })
  }

  return (
    <ActionCard
      title={localizedStrings.resumePermitRequest}
      info={
        <Box>
          <Box>{`${
            localizedStrings.suspendedBy
          }: ${suspendedBy.getFullName()}`}</Box>
          <Box>{`${localizedStrings.date}: ${datetimeString}`}</Box>
        </Box>
      }
      BottomComponent={
        <Flex gap={1}>
          <LoadingButton
            variant='outlined'
            color='error'
            fullWidth
            onClick={() => {
              showModal(
                <RejectResumePermitRequestModal
                  onConfirmReject={handleReject}
                />,
              )
            }}
            loading={rejectResumeRequest.isLoading}
            disabled={approveResumeRequest.isLoading}
          >
            {localizedStrings.reject}
          </LoadingButton>
          <LoadingButton
            variant='contained'
            color='primary'
            fullWidth
            loading={approveResumeRequest.isLoading}
            disabled={rejectResumeRequest.isLoading}
            onClick={handleApprove}
          >
            {localizedStrings.approve}
          </LoadingButton>
        </Flex>
      }
      sx={{ mb: 1.5, borderColor: 'primary.main', borderWidth: 2 }}
    />
  )
}
